import { useContext, useState } from 'react'

import { ModalContext, SidebarModal } from '@domain-group/suit-theme'

import { useSetState } from '@domain-group/react-hooks'

import type { HandleCallbackActionParams } from '@domain-group/javascript-utils'

import CanvaArtworkingLoadingModal from '../CanvaArtworkingLoadingModal/CanvaArtworkingLoadingModal'

import DefaultState from './screens/DefaultState'
import ExistingCampaign from './screens/ExistingCampaign'
import NewCampaign from './screens/NewCampaign'

const MODAL_KEY = 'CreateAgencyMarketingCampaignModal'
const CANVA_LOADING_MODAL_KEY = 'CanvaArtworkingLoadingModal'

const modalTitles = {
  NewCampaign: {
    title: 'Create',
    subtitle: 'New Campaign',
  },
  ExistingCampaign: {
    title: 'Add to',
    subtitle: 'Existing Campaign',
  },
}

type DefaultStateParams = {
  selectedScreen: null | 'NewCampaign' | 'ExistingCampaign',
}

const defaultState: DefaultStateParams = {
  selectedScreen: null,
}

type ModalCallbacks = {
  closeModal: () => void,
  createArtworkForExistingCampaign: (payload: HandleCallbackActionParams) => void,
  createCampaignAndArtwork: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const CreateAgencyMarketingCampaignModal = (props: ModalProps) => {
  const { callbacks, showModal } = props

  const [state, setState] = useSetState(defaultState)
  const { selectedScreen } = state

  const [showCanvaArtworkingLoadingModal, setShowCanvaArtworkingLoadingModal] = useState(false)

  const modalContext = useContext(ModalContext)
  const { modalData, callbacks: { setModalData } } = modalContext

  const modalPayload = modalData?.[MODAL_KEY] ?? {}
  const { agencyMarketingCampaignParams } = modalPayload
  const { createCampaignWithArtwork = true, hideComponents = [] } = agencyMarketingCampaignParams

  const closeCanvaArtworkingLoadingModal = () => setShowCanvaArtworkingLoadingModal(false)

  const screenCallbacks = {
    ...callbacks,
    closeCanvaArtworkingLoadingModal,
    showCanvaArtworkingLoadingModal: (payload: { modalTitle?: string, modalTitleSecondLine?: string } = {
      modalTitle: 'Creating',
      modalTitleSecondLine: 'Canva artwork',
    }) => {
      setModalData(CANVA_LOADING_MODAL_KEY, payload)
      setShowCanvaArtworkingLoadingModal(true)
    },
  }

  const mainModal = (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header
        callbacks={callbacks}
        title={selectedScreen ? modalTitles[selectedScreen].title : 'New Agency'}
        titleSecondLine={selectedScreen ? modalTitles[selectedScreen].subtitle : 'Marketing'}
      />

      {!selectedScreen && (
        <DefaultState
          callbacks={{ selectScreen: screen => setState({ selectedScreen: screen }) }}
          hideComponents={hideComponents}
        />
      )}

      {selectedScreen === 'ExistingCampaign' && (
        <ExistingCampaign
          agencyMarketingCampaignParams={agencyMarketingCampaignParams}
          // @ts-expect-error TS2741
          callbacks={screenCallbacks}
        />
      )}

      {selectedScreen === 'NewCampaign' && (
        <NewCampaign
          agencyMarketingCampaignParams={agencyMarketingCampaignParams}
          // @ts-expect-error TS2741
          callbacks={screenCallbacks}
          createCampaignWithArtwork={createCampaignWithArtwork}
        />
      )}
    </SidebarModal>
  )

  return (
    <>
      {mainModal}

      <CanvaArtworkingLoadingModal
        callbacks={{ closeModal: closeCanvaArtworkingLoadingModal }}
        showModal={showCanvaArtworkingLoadingModal}
      />
    </>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <CreateAgencyMarketingCampaignModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
